import { ChevronDownIcon, ChevronRightIcon, CloseIcon } from '@chakra-ui/icons';
import {
  Alert,
  AlertIcon,
  Box,
  Button,
  Center,
  Flex,
  FormControl,
  FormErrorMessage,
  IconButton,
  Input,
  Select,
  SimpleGrid,
  Stack,
  Text,
  useColorModeValue,
  Menu,
  MenuButton,
  MenuList,
  MenuItem,
  Icon
} from '@chakra-ui/react';
import { assetRepository, Department, Location, Organization, Vendor } from 'api/assetRepository';
import Card from 'components/card/Card';
import InputField from 'components/fields/InputField';

import { useAuth } from 'contexts/authContext';
import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { assetSchema, flattenZodErrors } from "validations/Validations";
import { z } from "zod";
export default function AddAssetForm() {
  const textColor = useColorModeValue('secondaryGray.900', 'white');
  const navigate = useNavigate();
  const inputBg = "#1B254B";
  const menuListBg = "#1B254B";
  const menuHoverBg = "#2B3674";
  const inputBorderColor = "#4A5568";


  const placeholderColor = useColorModeValue('gray.500', 'gray.400');
  const subMenuShadow = useColorModeValue("md", "dark-lg");
  const [isSubmitting, setIsSubmitting] = useState(false);

  const [hoveredType, setHoveredType] = useState<{ id: string; type: string; subType: string[] } | null>(null);
  const [selectedType, setSelectedType] = useState<{ id: string; type: string; subType: string[] } | null>(null);
  const today = new Date().toISOString().split("T")[0];
  const [mode, setMode] = useState<"normal" | "lease" | "view" | "renew">("normal");
  const isViewMode = mode === "view";
  const isRenewMode = mode === "renew";
  const isLeaseMode = mode === "lease";
  const [contractors, setContractors] = useState<{ contractorId: string; name: string }[]>([]);
  const [contractorSearch, setContractorSearch] = useState("");
  const [isLeasedFilter, setIsLeasedFilter] = useState<boolean>(false);
  const [assetData, setAssetData] = useState({
    assetEquipmentId: '',
    assetNameId: '',
    name: '',
    type: '',
    subType: '',
    orgId: '',
    orgName: '',
    locationId: '',
    location: '',
    departmentId: '',
    departmentName: '',
    vendorId: '',
    vendorName: '',
    installedDate: today,
    status: 'Active',
  });
  const [leaseData, setLeaseData] = useState({
    assetId: "",
    contractorId: "",
    contractorName: "",
    leaseStartDate: "",
    leaseEndDate: "",
  });
  const [errors, setErrors] = useState<Record<string, string>>({});

  const [departments, setDepartments] = useState<Department[]>([]);

  const [organizations, setOrganizations] = useState<Organization[]>([]);
  const [locations, setLocations] = useState<Location[]>([]);
  const location = useLocation();
  const [vendors, setVendors] = useState<Vendor[]>([]);
  const [vendorProducts, setVendorProducts] = useState<any[]>([]);

  const [centerAlertVisible, setCenterAlertVisible] = useState(false);
  const [centerAlertMessage, setCenterAlertMessage] = useState('');
  const [centerAlertStatus, setCenterAlertStatus] = useState<
    'success' | 'info' | 'warning' | 'error'
  >('success');
  const centerAlertTimerRef = useRef<number | null>(null);
  const { role, orgId, DepartmentId, LocationId } = useAuth();
  const isLeaseAllowed = role === "Admin" || role === "OrgAdmin" || role === "LocationAdmin" || role === "DeptUser";
  const isOrgAdmin = role === 'OrgAdmin';
  const isDeptUser = role === 'DeptUser';
  const isLocationAdmin = role === 'LocationAdmin';
  useEffect(() => {
    const state = location.state;

    if (!state) {
      setMode("normal");
      return;
    }

    const asset = state.asset;

    if (state.isRenew) {
      setMode("renew");
    } else if (state.mode === "view") {
      setMode("view");
    } else if (state.mode === "lease") {
      setMode("lease");
    }

    if (asset) {
      setLeaseData({
        assetId: asset?.assetId || "",
        contractorId: asset?.leased?.contractorId || "",
        contractorName: asset?.leased?.contractorName || "",
        leaseStartDate: asset?.leased?.leaseStartDate?.split("T")[0] || "",
        leaseEndDate: asset?.leased?.leaseEndDate?.split("T")[0] || "",
      });

      setAssetData({
        assetEquipmentId: asset.assetEquipmentId || '',
        assetNameId: asset.assetNameId || '',
        name: asset.name || '',
        type: asset.type || '',
        subType: asset.subType || '',
        orgId: asset.orgId || '',
        orgName: asset.orgName || '',
        locationId: asset.locationId || '',
        location: asset.location || '',
        departmentId: asset.departmentId || '',
        departmentName: asset.departmentName || '',
        vendorId: asset.vendorId || '',
        vendorName: asset.vendorName || '',
        installedDate: asset.installedDate || today,
        status: asset.status || '',
      });
    }

  }, [location.state]);

  useEffect(() => {
    if (mode === "lease") {
      assetRepository.getContractors()
        .then((data) => setContractors(data))
        .catch((error) => {

        });
    }
  }, [mode]);

  useEffect(() => {
    setContractorSearch(leaseData.contractorName || "");
  }, [leaseData.contractorName]);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const orgs = await assetRepository.getOrganizations();
        setOrganizations(orgs);

        const vends = await assetRepository.getVendors();

        setVendors(vends);
      } catch (error) {

      }
    };
    fetchData();
  }, [isLeasedFilter]);

  // Prefill organization for OrgAdmin + DeptUser
  useEffect(() => {
    if (!organizations.length) return;

    // Org Admin OR Dept User OR Location Admin
    if (isOrgAdmin || isDeptUser || isLocationAdmin) {
      const userOrg = organizations.find((o) => o.orgId === orgId);

      if (userOrg) {
        // Auto set organization
        setAssetData((prev) => ({
          ...prev,
          orgId: userOrg.orgId,
          orgName: userOrg.orgName,
        }));

        assetRepository.getLocations(userOrg.orgId).then((locs) => {

          if (isDeptUser) {
            const allDepts = locs.flatMap(
              (loc) => loc.departments || []
            );

            const userDept = allDepts.find(
              (dept) => dept.departmentId === DepartmentId
            );

            if (userDept) {
              setAssetData((prev) => ({
                ...prev,
                departmentId: userDept.departmentId,
                departmentName: userDept.departmentName,
              }));
            }

            const filteredLocs = locs.filter((loc) =>
              loc.departments?.some(
                (d) => d.departmentId === DepartmentId
              )
            );

            setLocations(filteredLocs);

            if (filteredLocs.length > 0) {
              const selectedLocation = filteredLocs[0];

              setDepartments(selectedLocation.departments || []);

              setAssetData((prev) => ({
                ...prev,

                // organization
                orgId: userOrg.orgId,
                orgName: userOrg.orgName,

                // department
                departmentId: userDept?.departmentId || "",
                departmentName: userDept?.departmentName || "",

                // location
                locationId: selectedLocation.locationId || "",
                location: selectedLocation.location || "",
              }));
            }
          }

          else if (isLocationAdmin) {
            setLocations(locs);
            const userLocation = locs.find(
              (loc) => loc.locationId === LocationId
            );

            if (userLocation) {
              setDepartments(userLocation.departments || [])
              setAssetData((prev) => ({
                ...prev,
                locationId: userLocation.locationId,
                location: userLocation.location,
              }));
            }
          }

          else if (isOrgAdmin) {
            setLocations(locs);
          }
        });
      }
    }
  }, [
    organizations,
    isOrgAdmin,
    isDeptUser,
    isLocationAdmin,
    orgId,
    DepartmentId,
    LocationId,
  ]);

  const handleVendorChange = async (vendorId: string) => {
    const selected = vendors.find((v) => v.vendorId === vendorId);
    setAssetData((prev) => ({
      ...prev,
      vendorId: selected?.vendorId || '',
      vendorName: selected?.name || '',
      name: '',
      assetNameId: '',
      type: '',
    }));
    setVendorProducts([]);
    try {
      const products = await assetRepository.getVendorProducts(vendorId);


      const normalized = (products || []).map((p: any) => ({
        productId: String(p.productId ?? p.id ?? p._id),
        productName: p.productName ?? p.name ?? p.title ?? '',
        type: p.productType ?? '',
      }));
      setVendorProducts(normalized);
    } catch (err) {
      setVendorProducts([]);
    }
  };


  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>) => {
    const { name, value } = e.target;
    setAssetData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleOrgChange = async (orgId: string) => {
    const selectedOrg = organizations.find((o) => o.orgId === orgId);
    setAssetData((prev) => ({
      ...prev,
      orgId,
      orgName: selectedOrg?.orgName || '',
      locationId: '',
      location: '',
      departmentId: '',
      departmentName: '',
    }));

    try {
      const locs = await assetRepository.getLocations(orgId);
      setLocations(locs);
      setDepartments([]);
    } catch (error) {
      setLocations([]);
      setDepartments([]);
    }
  };

  const handleLocationChange = (locationId: string) => {
    const selectedLocation = locations.find(
      (loc) => loc.locationId === locationId
    );

    setAssetData((prev) => ({
      ...prev,
      locationId: selectedLocation?.locationId || '',
      location: selectedLocation?.location || '',
      departmentId: '',
      departmentName: '',
    }));
    console.log(selectedLocation)
    console.log(selectedLocation?.departments)

    const validDepartments = selectedLocation?.departments?.filter(
      (dept) => dept.departmentId,
    ) || [];
    setDepartments(validDepartments);
  };


  const handleDepartmentChange = (departmentId: string) => {
    const selected = departments.find((d) => d.departmentId === departmentId);
    setAssetData((prev) => ({
      ...prev,
      departmentId: selected?.departmentId || '',
      departmentName: selected?.departmentName || '',
    }));
  };

  const matchesContractorSearch = (contractorName: string, searchValue: string) => {
    const normalizedSearch = searchValue.trim().toLowerCase();
    if (!normalizedSearch) return true;

    const normalizedName = contractorName.toLowerCase();
    const initials = contractorName
      .split(/\s+/)
      .filter(Boolean)
      .map((part) => part[0]?.toLowerCase() || "")
      .join("");

    return normalizedName.includes(normalizedSearch) || initials.startsWith(normalizedSearch);
  };

  const filteredContractors = contractors.filter((contractor) =>
    matchesContractorSearch(contractor.name, contractorSearch)
  );
  const contractorSuggestions = contractorSearch.trim()
    ? filteredContractors.slice(0, 3)
    : [];

  const handleContractorSelect = (contractorId: string, contractorName: string) => {
    setLeaseData((prev) => ({
      ...prev,
      contractorId,
      contractorName,
    }));
    setContractorSearch(contractorName);
  };
  const contractorOptions = contractors.map((contractor) => ({
    value: contractor.contractorId,
    label: contractor.name,
  }));

  const showCenteredAlert = (
    msg: string,
    status: 'success' | 'info' | 'warning' | 'error' = 'success',
    duration = 3000
  ) => {
    if (centerAlertTimerRef.current) {
      window.clearTimeout(centerAlertTimerRef.current);
      centerAlertTimerRef.current = null;
    }
    setCenterAlertMessage(msg);
    setCenterAlertStatus(status);
    setCenterAlertVisible(true);
    centerAlertTimerRef.current = window.setTimeout(() => {
      setCenterAlertVisible(false);
      centerAlertTimerRef.current = null;
    }, duration);
  };

  const handleSubmit = async () => {
    if (isSubmitting) return;
    setIsSubmitting(true);

    try {
      setErrors({});

      const validated = assetSchema.parse({
        ...assetData,
        assetEquipmentId: assetData.assetEquipmentId.trim(),
      });

      await assetRepository.addAsset(validated as any);

      showCenteredAlert("Asset added successfully!", "success", 2000);

      // Reset form fields
      setAssetData({
        assetEquipmentId: '',
        assetNameId: '',
        name: '',
        type: '',
        subType: '',
        orgId: '',
        orgName: '',
        locationId: '',
        location: '',
        departmentId: '',
        departmentName: '',
        vendorId: '',
        vendorName: '',
        installedDate: today,
        status: '',
      });
      setVendorProducts([]);
      setSelectedType(null);
      setHoveredType(null);

      setTimeout(() => {
        navigate("/admin/asset-management/assetList");
      }, 1200);
    } catch (error: any) {
      if (error instanceof z.ZodError) {
        setErrors(flattenZodErrors(error));

      } else if (error.response?.data) {
        const backendMessage = error.response.data;

        if (
          backendMessage.toLowerCase().includes("equipment") ||
          backendMessage.toLowerCase().includes("duplicate")
        ) {
          setErrors((prev) => ({
            ...prev,
            assetEquipmentId: backendMessage,
          }));
          showCenteredAlert(backendMessage, "error", 2000);

        } else {
          showCenteredAlert(backendMessage, "error", 2000);
        }

      } else {
        showCenteredAlert("Failed to add asset.", "error", 2000);
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleLeaseSubmit = async () => {
    try {
      if (!leaseData.assetId) {
        showCenteredAlert("Invalid asset", "warning");
        return;
      }

      if (isViewMode) {
        showCenteredAlert("View mode only", "info");
        return;
      }

      if (!leaseData.leaseEndDate) {
        showCenteredAlert("Lease end date required", "warning");
        return;
      }

      if (isRenewMode) {
        // RENEW FLOW
        await assetRepository.UpdateLease(leaseData.assetId, {
          contractorId: leaseData.contractorId,
          contractorName: leaseData.contractorName,
          leaseStartDate: leaseData.leaseStartDate,
          leaseEndDate: leaseData.leaseEndDate,
        });

      } else {
        // NEW LEASE FLOW
        await assetRepository.leaseAssetToContractor({
          assetId: leaseData.assetId,
          contractorId: leaseData.contractorId,
          contractorName: leaseData.contractorName,
          leaseStartDate: leaseData.leaseStartDate,
          leaseEndDate: leaseData.leaseEndDate,
        });
      }
      showCenteredAlert(
        isRenewMode ? "Lease renewed successfully!" : "Asset leased successfully!",
        "success"
      );

      setTimeout(() => {
        navigate("/admin/asset-management/assetList");
      }, 1200);

    } catch (error: any) {
      showCenteredAlert(
        error?.response?.data || "Failed to process lease",
        "error"
      );
    }
  };

  return (
    <Flex
      direction="column"
      // minH="100vh"
      align="center"
      justify="flex-start"
      position="relative"
     
    >
      <Box
        position="absolute"
        top="90"
        w="100%"
        h="40vh"
        bgGradient="linear(to-b, brand.400, brand.600)"
        borderRadius="20px"
      />

      <Card
        w={{ sm: '330px', md: '700px', lg: '850px' }}
        p="30px"
        mt={{ md: '100px' }}
        mx="auto"
        zIndex={10}
        borderRadius="16px" >
        <Text color={textColor} fontSize="2xl" fontWeight="700" mb="20px">
          {mode === "lease" ? "Lease Asset" : mode === "view" ? "View Lease" : mode === "renew" ? "Renew Lease" : "Add Asset"}
        </Text>

        <IconButton
          icon={<CloseIcon />}
          aria-label="Close"
          position="absolute"
          top="10px"
          right="10px"
          size="sm"
          onClick={() => navigate('/admin/asset-management/assetList')}
        />
        {mode === "normal" && (
          <>
            <Flex direction="column" w="100%">
              <SimpleGrid columns={{ base: 1, md: 2 }} gap="20px">
                <Stack direction="column" gap="20px">
                  {/* Asset Equipment ID */}
                  <FormControl isInvalid={!!errors.assetEquipmentId}>
                    <InputField
                      label='Asset Equipment ID'
                      name="assetEquipmentId"
                      placeholder="e.g. ABC123XYZ45678"
                      value={assetData.assetEquipmentId}
                      onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                        const value = e.target.value
                          .replace(/[^A-Za-z0-9]/g, "")
                          .slice(0, 15);

                        setAssetData(prev => ({
                          ...prev,
                          assetEquipmentId: value
                        }));
                      }}
                      mb="0"
                    />
                    {errors.assetEquipmentId && <FormErrorMessage>{errors.assetEquipmentId}</FormErrorMessage>}
                  </FormControl>
                  {/*Location/Plant*/}
                  <FormControl isInvalid={!!errors.locationId}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Plant
                    </Text>
                    <Select
                      placeholder="Select Plant"
                      value={assetData.locationId || ''}
                      bg={inputBg}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      isDisabled={isLocationAdmin || isDeptUser}
                    >
                      {locations.length > 0 ? (
                        locations.map((loc) => (
                          <option key={loc.locationId} value={loc.locationId} style={{ backgroundColor: menuListBg, color: textColor }}>
                            {loc.location}
                          </option>
                        ))
                      ) : (
                        <option disabled style={{ backgroundColor: menuListBg, color: textColor }}>No locations available</option>
                      )}
                    </Select>
                    {errors.locationId && <FormErrorMessage>{errors.locationId}</FormErrorMessage>}
                  </FormControl>
                  {/* Vendor */}
                  <FormControl isInvalid={!!errors.vendorId}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Assign Vendor
                    </Text>
                    <Select
                      placeholder="Select vendor"
                      bg={inputBg}
                      h="44px"
                      fontSize="sm"
                      value={assetData.vendorId || ''}
                      onChange={(e) => handleVendorChange(e.target.value)}
                    >
                      {vendors.map((vendor) => (
                        <option key={vendor.vendorId} value={vendor.vendorId} style={{ backgroundColor: menuListBg, color: textColor }}>
                          {vendor.name}
                        </option>
                      ))}
                    </Select>
                    {errors.vendorId && <FormErrorMessage>{errors.vendorId}</FormErrorMessage>}
                  </FormControl>
                  {/* Asset Type */}
                  <FormControl isInvalid={!!errors.type}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Asset Type
                    </Text>

                    <Input
                      value={assetData.type || ""}
                      placeholder="Auto-filled from Asset Name"
                      bg={inputBg}
                      color={textColor}
                      borderColor={inputBorderColor}
                      h="44px"
                      fontSize="sm"
                      isReadOnly
                    />

                    {errors.type && <FormErrorMessage>{errors.type}</FormErrorMessage>}
                  </FormControl>
                  {/* Installed Date */}
                  <FormControl isInvalid={!!errors.installedDate}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Installed Date
                    </Text>
                    <Input
                      type="date"
                      name="installedDate"
                      id="installedDate"
                      placeholder="Select Date"
                      bg={inputBg}
                      textColor={textColor}
                      borderColor={inputBorderColor}
                      h="44px"
                      fontSize="sm"
                      value={assetData.installedDate}
                      max={new Date().toISOString().split("T")[0]}
                      onChange={handleInputChange}
                    />
                    {errors.installedDate && <FormErrorMessage>{errors.installedDate}</FormErrorMessage>}
                  </FormControl>
                </Stack>

                <Stack direction="column" gap="20px" mt={{ base: '0', md: '4px' }}>
                  {/* Organization */}
                  <FormControl isInvalid={!!errors.orgId}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Organization
                    </Text>
                    <Select
                      placeholder="Select Organization"
                      bg={inputBg}
                      value={assetData.orgId || ''}
                      onChange={(e) => handleOrgChange(e.target.value)}
                      isDisabled={isOrgAdmin || isDeptUser || isLocationAdmin}
                    >
                      {organizations.map((org) => (
                        <option key={org.orgId} value={org.orgId} style={{ backgroundColor: menuListBg, color: textColor }}>
                          {org.orgName}
                        </option>

                      ))}
                    </Select>
                    {errors.orgId && <FormErrorMessage>{errors.orgId}</FormErrorMessage>}
                  </FormControl>

                  {/* Department */}
                  <FormControl isInvalid={!!errors.departmentId}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Department
                    </Text>
                    <Select
                      placeholder="Select Department"
                      bg={inputBg}
                      value={assetData.departmentId || ""}
                      onChange={(e) => handleDepartmentChange(e.target.value)}
                      isDisabled={isDeptUser}
                    >
                      {departments.length > 0 ? (
                        departments.map((dept) => (
                          <option key={dept.departmentId} value={dept.departmentId} style={{ backgroundColor: menuListBg, color: textColor }}>
                            {dept.departmentName}
                          </option>
                        ))
                      ) : (
                        <option disabled style={{ backgroundColor: menuListBg, color: textColor }}>No departments available</option>
                      )}
                    </Select>
                    {errors.departmentId && <FormErrorMessage>{errors.departmentId}</FormErrorMessage>}
                  </FormControl>
                  {/* Asset Name (Vendor Product) */}
                  <FormControl isInvalid={!!errors.assetNameId}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Asset Name
                    </Text>
                    <Select
                      placeholder="Select Asset (Vendor Product)"
                      bg={inputBg}
                      value={assetData.assetNameId || ''}
                      onChange={(e: React.ChangeEvent<HTMLSelectElement>) => {
                        const pid = e.target.value;

                        const selected = vendorProducts.find(
                          (p: any) => String(p.productId) === String(pid)
                        );
                        setAssetData((prev) => ({
                          ...prev,
                          assetNameId: pid,
                          name: selected?.productName ?? "",
                          type: selected?.type ?? "",
                        }));
                      }}
                    >
                      {vendorProducts.length > 0 ? (
                        vendorProducts.map((p: any) => (
                          <option key={p.productId} value={p.productId} style={{ backgroundColor: menuListBg, color: textColor }}>
                            {p.productName}
                          </option>
                        ))
                      ) : (
                        <option disabled style={{ backgroundColor: menuListBg, color: textColor }}>No products available</option>
                      )}
                    </Select>
                    {errors.assetNameId && <FormErrorMessage>{errors.assetNameId}</FormErrorMessage>}
                  </FormControl>
                  {/* Status */}
                  <FormControl isInvalid={!!errors.status}>
                    <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                      Status
                    </Text>
                    <Select
                      name="status"
                      placeholder="Select status"
                      bg={inputBg}
                      h="44px"
                      fontSize="sm"
                      color={assetData.status ? textColor : placeholderColor}
                      value={assetData.status}
                      onChange={handleInputChange}
                      style={{ color: assetData.status ? textColor : placeholderColor, backgroundColor: inputBg }}>
                      <option>Active</option>
                      <option >Maintenance</option>
                      <option >Inactive</option>
                    </Select>
                    {errors.status && <FormErrorMessage>{errors.status}</FormErrorMessage>}
                  </FormControl>
                </Stack>
              </SimpleGrid>

              {/* Submit Button */}
              <Flex justify="center" gap="20px" mt="24px">
                <Button
                  variant="darkBrand"
                  fontSize="sm"
                  borderRadius="16px"
                  w={{ base: '128px', md: '148px' }}
                  h="46px"
                  onClick={handleSubmit}
                  isLoading={isSubmitting}
                  isDisabled={isSubmitting}
                >
                  Submit
                </Button>
              </Flex>
            </Flex>
          </>
        )}
        {(mode === "lease" || mode === "view" || mode === "renew") && isLeaseAllowed && (
          <Flex direction="column" w="100%">

            <SimpleGrid columns={{ base: 1, md: 1 }} gap="20px">
              <Stack direction="column" gap="20px">

                {/* Lease To Contractor */}
                {/* Lease To Contractor */}
                <FormControl>
                  <Text
                    fontSize="sm"
                    fontWeight="bold"
                    mb="8px"
                    color={textColor}
                  >
                    Lease To Contractor
                  </Text>

                  <Menu matchWidth>
                    <MenuButton
                      as={Button}
                      rightIcon={<ChevronDownIcon />}
                      w="100%"
                      h="44px"
                      px="16px"
                      textAlign="left"
                      fontSize="sm"
                      fontWeight="normal"
                      borderRadius="8px"
                      bg={inputBg}
                      color={leaseData.contractorName ? textColor : placeholderColor}
                      border="1px solid"
                      borderColor={inputBorderColor}
                      _hover={{
                        bg: inputBg,
                        borderColor: inputBorderColor,
                      }}
                      _active={{
                        bg: inputBg,
                        borderColor: inputBorderColor,
                      }}
                      _focus={{
                        bg: inputBg,
                        borderColor: inputBorderColor,
                        boxShadow: "none",
                      }}
                      isDisabled={isViewMode || isRenewMode}
                    >
                      <Flex justify="space-between" align="center" w="100%">
                        <Text
                          color={
                            leaseData.contractorName
                              ? textColor
                              : placeholderColor
                          }
                        >
                          {leaseData.contractorName || "Select Contractor"}
                        </Text>
                      </Flex>
                    </MenuButton>

                    <MenuList
                      bg={menuListBg}
                      border="1px solid"
                      borderColor={inputBorderColor}
                      borderRadius="8px"
                      p="4px"
                      maxH="132px"
                      overflowY="auto"
                      zIndex={9999}
                    >
                      {contractors.length > 0 ? (
                        contractors.map((contractor) => (
                          <MenuItem
                            key={contractor.contractorId}
                            bg="transparent"
                            color={textColor}
                            borderRadius="6px"
                            _hover={{
                              bg: menuHoverBg,
                            }}
                            _focus={{
                              bg: menuHoverBg,
                            }}
                            onClick={() =>
                              setLeaseData((prev) => ({
                                ...prev,
                                contractorId: contractor.contractorId,
                                contractorName: contractor.name,
                              }))
                            }
                          >
                            {contractor.name}
                          </MenuItem>
                        ))
                      ) : (
                        <MenuItem
                          isDisabled
                          bg="transparent"
                          color="gray.400"
                        >
                          No contractors available
                        </MenuItem>
                      )}
                    </MenuList>
                  </Menu>
                </FormControl>

                {/* Lease Start Date */}
                <FormControl>
                  <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                    Lease Start Date
                  </Text>
                  <Input
                    type="date"
                    min={today}
                    bg={inputBg}
                    color={textColor}
                    borderColor={inputBorderColor}
                    h="44px"
                    fontSize="sm"
                    value={leaseData.leaseStartDate}
                    isDisabled={isViewMode || isRenewMode}
                    onChange={(e) => {
                      const newStartDate = e.target.value;

                      setLeaseData((prev) => ({
                        ...prev,
                        leaseStartDate: newStartDate,

                        leaseEndDate:
                          prev.leaseEndDate && prev.leaseEndDate < newStartDate
                            ? ""
                            : prev.leaseEndDate,
                      }));
                    }}
                  />
                </FormControl>
              </Stack>

              <Stack direction="column" gap="20px">

                {/* Lease End Date */}
                <FormControl>
                  <Text fontSize="sm" fontWeight="bold" mb="8px" color={textColor}>
                    Lease End Date
                  </Text>
                  <Input
                    type="date"
                    min={leaseData.leaseStartDate || today}
                    bg={inputBg}
                    color={textColor}
                    borderColor={inputBorderColor}
                    h="44px"
                    fontSize="sm"
                    isDisabled={isViewMode}
                    value={leaseData.leaseEndDate}
                    onChange={(e) =>
                      setLeaseData((prev) => ({
                        ...prev,
                        leaseEndDate: e.target.value,
                      }))
                    }
                  />
                </FormControl>

              </Stack>
            </SimpleGrid>

            {/* Submit Button */}
            <Flex justify="center" align="center" mt="30px">
              {isViewMode ? (
                <Text fontSize="md" fontWeight="600" color="gray.500">
                  This asset is already leased
                </Text>
              ) : (
                <Button
                  variant="darkBrand"
                  fontSize="sm"
                  borderRadius="16px"
                  w={{ base: "140px", md: "160px" }}
                  h="46px"
                  onClick={handleLeaseSubmit}
                >
                  {isRenewMode ? "Renew Lease" : "Submit Lease"}
                </Button>
              )}
            </Flex>
          </Flex>
        )}
      </Card>
      {centerAlertVisible && (
        <Center
          position="fixed"
          top="0"
          left="0"
          right="0"
          bottom="0"
          zIndex={60}
          pointerEvents="none"
        >
          <Alert
            status={centerAlertStatus}
            variant="solid"
            flexDirection="column"
            alignItems="center"
            justifyContent="center"
            textAlign="center"
            width="600px"
            borderRadius="14px"
            boxShadow="xl"
            py={10}
            px={16}
            pointerEvents="auto"
          >
            <AlertIcon boxSize="40px" mr={0} />

            <Text fontSize="lg" fontWeight="700" mt={1}>
              {centerAlertMessage}
            </Text>
          </Alert>
        </Center>
      )}
    </Flex>
  );
}